import { HttpClient, HttpParams } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

import { AppConfig, CMPS_APP_CONFIG } from '../app.config';
import { ObjectDateTransformer } from '../coaching-insight/coaching-insight.model';
import { catchErrorLogRet, mapRetrieve } from '../common/rest.operators';
import { Platform } from '../platform-call';

import {
    CoachingTranscript,
    TranscriptSummary,
    TranscriptSummaryParams,
} from './general';

@Injectable()
export class TranscriptService {
    constructor(
        private readonly _http: HttpClient,
        @Inject(CMPS_APP_CONFIG) private readonly _appConfig: AppConfig,
    ) {}

    retrieveSummary(
        summaryParams: TranscriptSummaryParams,
    ): Observable<TranscriptSummary> {
        let params = new HttpParams();
        if (summaryParams.startDate != null) {
            params = params.set('startDate', summaryParams.startDate.toJSON());
        }
        if (summaryParams.endDate != null) {
            params = params.set('endDate', summaryParams.endDate.toJSON());
        }
        return this._http
            .get(
                `${this._appConfig.API_ENDPOINTS.COACHING}/${summaryParams.userId}/transcript-summary`,
                { params },
            )
            .pipe(
                mapRetrieve(),
                map((ret) => {
                    const obj = {
                        transcripts: {
                            startDate: null,
                            endDate: null,
                            createdOn: null,
                        },
                    };
                    return new ObjectDateTransformer<TranscriptSummary>({
                        draft: obj,
                        active: obj,
                        closed: obj,
                        nonAssociated: obj,
                    }).transform(ret);
                }),
                catchErrorLogRet({}),
            );
    }

    retrieve(
        platform: Platform,
        transcriptId: number,
    ): Observable<CoachingTranscript> {
        return this._http
            .get<CoachingTranscript>(
                `${this._appConfig.API_ENDPOINTS.COACHING}/${platform}/transcripts/${transcriptId}`,
            )
            .pipe(
                map((ret) => {
                    return new ObjectDateTransformer<CoachingTranscript>({
                        startDate: null,
                        endDate: null,
                        createdOn: null,
                        entries: {
                            startTime: null,
                            endTime: null,
                        },
                    }).transform(ret);
                }),
                catchErrorLogRet({}),
            );
    }

    update(transcript: CoachingTranscript): Observable<CoachingTranscript> {
        return this._http
            .put<CoachingTranscript>(
                `${this._appConfig.API_ENDPOINTS.COACHING}/${transcript.platform}/transcripts/${transcript.transcriptId}`,
                transcript,
            )
            .pipe(
                map((ret) => {
                    return new ObjectDateTransformer<CoachingTranscript>({
                        startDate: null,
                        endDate: null,
                        createdOn: null,
                        entries: {
                            startTime: null,
                            endTime: null,
                        },
                    }).transform(ret);
                }),
                catchErrorLogRet({}),
            );
    }

    delete(platform: Platform, transcriptId: number): Observable<boolean> {
        return this._http
            .delete(
                `${this._appConfig.API_ENDPOINTS.COACHING}/${platform}/transcripts/${transcriptId}`,
            )
            .pipe(catchErrorLogRet(false));
    }

    deleteEntries(
        platform: Platform,
        transcriptId: number,
        transcriptEntryIds: number[],
    ): Observable<boolean> {
        return this._http
            .post(
                `${this._appConfig.API_ENDPOINTS.COACHING}/${platform}/transcripts/${transcriptId}/entries`,
                transcriptEntryIds,
            )
            .pipe(catchErrorLogRet(false));
    }
}
